import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from './api';

function clsx(...xs) { return xs.filter(Boolean).join(' '); }

function Badge({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = 'primary', disabled = false, className = '', type='button' }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center';
  const styles =
    variant === 'primary'
      ? 'bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60'
      : variant === 'danger'
      ? 'bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60'
      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 disabled:opacity-60';
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={clsx(base, styles, className)}>
      {children}
    </button>
  );
}

/** scroll-safe modal */
function Modal({ open, title, children, onClose, wide=false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[240] bg-black/40 overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-3 sm:p-6">
        <div className={clsx(
          'bg-white w-full rounded-2xl shadow-xl border',
          wide ? 'max-w-4xl' : 'max-w-2xl',
          'max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] flex flex-col'
        )}>
          <div className="p-4 border-b flex items-center justify-between gap-3 shrink-0">
            <div className="font-semibold text-slate-900">{title}</div>
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
          <div className="p-4 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/** ---- Date helpers (avoid timezone day-shift) ---- */
function toISODateUTC(d) {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const day = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Convert YYYY-MM-DD from <input type="date"> to ISO string at UTC midnight */
function dateInputToIsoUtc(dateStr) {
  if (!dateStr) return null;
  // force UTC midnight; prevents "previous day" issues in local timezones
  return new Date(`${dateStr}T00:00:00Z`).toISOString();
}

function isExpiredUtc(expiresAt) {
  if (!expiresAt) return false;
  const exp = toISODateUTC(expiresAt);
  if (!exp) return false;
  const today = toISODateUTC(new Date());
  return exp < today;
}

const DISCIPLINE_OPTIONS = [
  { value: 'PROG_EI', label: 'PROG_EI' },
  { value: 'PROG_PI', label: 'PROG_PI' },
  { value: 'PROG_ABA', label: 'PROG_ABA' },
  { value: 'PROG_OT', label: 'PROG_OT' },
  { value: 'PROG_PT', label: 'PROG_PT' },
  { value: 'PROG_ST', label: 'PROG_ST' },
  { value: '__custom__', label: 'Custom…' },
];

export default function SuperLicenses() {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [loading, setLoading] = useState(false);

  const [discLic, setDiscLic] = useState([]);
  const [instrLic, setInstrLic] = useState([]);

  // add discipline
  const [discCode, setDiscCode] = useState('PROG_PI');
  const [discCustom, setDiscCustom] = useState('');
  const [discActive, setDiscActive] = useState(1);
  const [discStarts, setDiscStarts] = useState(toISODateUTC(new Date()));
  const [discExpires, setDiscExpires] = useState('');
  const [discNotes, setDiscNotes] = useState('Set via SuperLicenses UI');

  // add instrument license
  const [globalList, setGlobalList] = useState([]);
  const [pickInstrumentId, setPickInstrumentId] = useState('');
  const [instrActive, setInstrActive] = useState(1);
  const [instrStarts, setInstrStarts] = useState(toISODateUTC(new Date()));
  const [instrExpires, setInstrExpires] = useState('');
  const [instrNotes, setInstrNotes] = useState('Set via SuperLicenses UI');

  // edit modals
  const [editDiscOpen, setEditDiscOpen] = useState(false);
  const [editInstrOpen, setEditInstrOpen] = useState(false);

  const [editDisc, setEditDisc] = useState(null);   // { organization_id, discipline_code, starts_at, expires_at, is_active, notes }
  const [editInstr, setEditInstr] = useState(null); // { organization_id, instrument_id, starts_at, expires_at, is_active, notes, instrument_code, version, name }

  const pickedOrg = useMemo(() => orgs.find((o) => String(o.id) === String(orgId)), [orgs, orgId]);

  async function loadOrgs() {
    try {
      const data = await apiGet('/super/organizations_name');
      setOrgs(data.organizations || []);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to load organizations');
    }
  }

  async function loadGlobalInstruments() {
    try {
      const data = await apiGet('/super/instruments', { active: 1 });
      const globals = (data.items || []).filter((x) => x.organization_id == null);
      setGlobalList(globals);
      if (pickInstrumentId && !globals.some((x) => String(x.id) === String(pickInstrumentId))) {
        setPickInstrumentId('');
      }
    } catch (e) {
      console.error(e);
      setGlobalList([]);
      setPickInstrumentId('');
    }
  }

  async function loadLicenses(selectedOrgId) {
    if (!selectedOrgId) return;
    setLoading(true);
    try {
      const data = await apiGet('/super/licenses', { organization_id: Number(selectedOrgId) });
      setDiscLic(data.discipline_licenses || []);
      setInstrLic(data.instrument_licenses || []);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadOrgs(); loadGlobalInstruments(); }, []); // eslint-disable-line
  useEffect(() => { if (orgId) loadLicenses(orgId); }, [orgId]); // eslint-disable-line

  const orgLabel = useMemo(() => {
    if (!pickedOrg) return '';
    return pickedOrg.is_active ? 'active org' : 'inactive org';
  }, [pickedOrg]);

  const discCodeFinal = useMemo(() => {
    if (discCode === '__custom__') return (discCustom || '').trim();
    return String(discCode || '').trim();
  }, [discCode, discCustom]);

  async function upsertDiscipline(payloadOverride = null) {
    const code = payloadOverride?.discipline_code ?? discCodeFinal;
    if (!orgId || !code) return alert('Pick org and discipline_code');

    try {
      setLoading(true);
      await apiPost('/super/licenses/discipline', {
        organization_id: Number(orgId),
        discipline_code: code,
        starts_at: payloadOverride?.starts_at ?? dateInputToIsoUtc(discStarts) ?? new Date().toISOString(),
        expires_at: payloadOverride?.expires_at ?? (discExpires ? dateInputToIsoUtc(discExpires) : null),
        is_active: payloadOverride?.is_active ?? (discActive ? 1 : 0),
        notes: payloadOverride?.notes ?? (discNotes || null),
      });
      await loadLicenses(orgId);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to save discipline license');
    } finally {
      setLoading(false);
    }
  }

  async function upsertInstrument(payloadOverride = null) {
    const instId = payloadOverride?.instrument_id ?? pickInstrumentId;
    if (!orgId || !instId) return alert('Pick org and instrument');

    try {
      setLoading(true);
      await apiPost('/super/licenses/instrument', {
        organization_id: Number(orgId),
        instrument_id: Number(instId),
        starts_at: payloadOverride?.starts_at ?? dateInputToIsoUtc(instrStarts) ?? new Date().toISOString(),
        expires_at: payloadOverride?.expires_at ?? (instrExpires ? dateInputToIsoUtc(instrExpires) : null),
        is_active: payloadOverride?.is_active ?? (instrActive ? 1 : 0),
        notes: payloadOverride?.notes ?? (instrNotes || null),
      });
      await loadLicenses(orgId);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to save instrument license');
    } finally {
      setLoading(false);
    }
  }

  function openEditDisciplineRow(d) {
    setEditDisc({
      organization_id: Number(orgId),
      discipline_code: d.discipline_code,
      starts_at: d.starts_at || null,
      expires_at: d.expires_at || null,
      is_active: d.is_active ? 1 : 0,
      notes: d.notes || '',
    });
    setEditDiscOpen(true);
  }

  function openEditInstrumentRow(l) {
    setEditInstr({
      organization_id: Number(orgId),
      instrument_id: l.instrument_id,
      starts_at: l.starts_at || null,
      expires_at: l.expires_at || null,
      is_active: l.is_active ? 1 : 0,
      notes: l.notes || '',
      instrument_code: l.instrument_code,
      version: l.version,
      name: l.name,
      form_kind: l.form_kind,
      discipline_code: l.discipline_code,
    });
    setEditInstrOpen(true);
  }

  async function saveEditDisc() {
    if (!editDisc?.discipline_code) return;
    await upsertDiscipline({
      discipline_code: String(editDisc.discipline_code).trim(),
      starts_at: editDisc.starts_at ? dateInputToIsoUtc(toISODateUTC(editDisc.starts_at)) : dateInputToIsoUtc(toISODateUTC(new Date())),
      expires_at: editDisc.expires_at ? dateInputToIsoUtc(toISODateUTC(editDisc.expires_at)) : null,
      is_active: editDisc.is_active ? 1 : 0,
      notes: editDisc.notes || null,
    });
    setEditDiscOpen(false);
    setEditDisc(null);
  }

  async function saveEditInstr() {
    if (!editInstr?.instrument_id) return;
    await upsertInstrument({
      instrument_id: Number(editInstr.instrument_id),
      starts_at: editInstr.starts_at ? dateInputToIsoUtc(toISODateUTC(editInstr.starts_at)) : dateInputToIsoUtc(toISODateUTC(new Date())),
      expires_at: editInstr.expires_at ? dateInputToIsoUtc(toISODateUTC(editInstr.expires_at)) : null,
      is_active: editInstr.is_active ? 1 : 0,
      notes: editInstr.notes || null,
    });
    setEditInstrOpen(false);
    setEditInstr(null);
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* ✅ Neutral header */}
      <div className="bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-semibold text-slate-900 truncate">Licenses</div>
            <div className="mt-1 text-sm text-slate-600">
              Assign discipline + instrument licenses per organization. Instrument licenses apply to GLOBAL templates only.
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
            <Button
              variant="secondary"
              onClick={() => { loadOrgs(); loadGlobalInstruments(); if (orgId) loadLicenses(orgId); }}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Reload'}
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-2">
          <div className="lg:col-span-7">
            <label className="text-xs text-slate-600">Organization</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
            >
              <option value="">Select organization…</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}{o.is_active ? '' : ' [inactive]'}
                </option>
              ))}
            </select>
          </div>

          <div className="lg:col-span-5 flex items-end gap-2">
            {pickedOrg ? (
              <>
                <Badge tone={pickedOrg.is_active ? 'green' : 'red'}>{orgLabel}</Badge>
                <span className="text-xs text-slate-500">org_id={pickedOrg.id}</span>
              </>
            ) : (
              <span className="text-sm text-slate-500">Pick an organization to manage licenses.</span>
            )}
          </div>
        </div>
      </div>

      {!orgId ? (
        <div className="mt-4 text-slate-600">Select an organization to manage licenses.</div>
      ) : (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Discipline */}
          <div className="bg-white border rounded-2xl shadow-sm p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">Discipline licenses</div>
                <div className="text-xs text-slate-500 mt-1">
                  Controls access by <span className="font-mono">discipline_code</span> gating.
                </div>
              </div>
              <Badge tone="slate">upsert</Badge>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-6 gap-2">
              <div className="sm:col-span-2">
                <label className="text-[11px] text-slate-600">Discipline</label>
                <select
                  className="w-full px-3 py-2 rounded-xl border bg-white"
                  value={discCode}
                  onChange={(e) => setDiscCode(e.target.value)}
                >
                  {DISCIPLINE_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
                {discCode === '__custom__' ? (
                  <input
                    className="mt-2 w-full px-3 py-2 rounded-xl border"
                    value={discCustom}
                    onChange={(e) => setDiscCustom(e.target.value)}
                    placeholder="Enter discipline_code (e.g. PROG_X)"
                  />
                ) : null}
              </div>

              <div className="sm:col-span-1">
                <label className="text-[11px] text-slate-600">Active</label>
                <select
                  className="w-full px-3 py-2 rounded-xl border"
                  value={discActive ? 1 : 0}
                  onChange={(e) => setDiscActive(Number(e.target.value))}
                >
                  <option value={1}>active</option>
                  <option value={0}>inactive</option>
                </select>
              </div>

              <div className="sm:col-span-1">
                <label className="text-[11px] text-slate-600">Starts</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={discStarts}
                  onChange={(e) => setDiscStarts(e.target.value)}
                />
              </div>

              <div className="sm:col-span-1">
                <label className="text-[11px] text-slate-600">Expires</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={discExpires}
                  onChange={(e) => setDiscExpires(e.target.value)}
                />
              </div>

              <div className="sm:col-span-1 flex items-end">
                <Button variant="primary" onClick={() => upsertDiscipline()} disabled={loading || !discCodeFinal}>
                  Save
                </Button>
              </div>

              <div className="sm:col-span-6">
                <label className="text-[11px] text-slate-600">Notes</label>
                <input
                  className="w-full px-3 py-2 rounded-xl border"
                  value={discNotes}
                  onChange={(e) => setDiscNotes(e.target.value)}
                  placeholder="Optional notes…"
                />
              </div>
            </div>

            <div className="mt-3 border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-left">Active</th>
                    <th className="p-2 text-left">Starts</th>
                    <th className="p-2 text-left">Expires</th>
                    <th className="p-2 text-left">Notes</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {discLic.map((d) => {
                    const expired = isExpiredUtc(d.expires_at);
                    return (
                      <tr key={d.id || d.discipline_code} className="border-b">
                        <td className="p-2"><Badge>{d.discipline_code}</Badge></td>
                        <td className="p-2">
                          {d.is_active ? <Badge tone="green">active</Badge> : <Badge tone="red">inactive</Badge>}
                        </td>
                        <td className="p-2">
                          {d.starts_at ? <span className="text-slate-700">{toISODateUTC(d.starts_at)}</span> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-2">
                          {d.expires_at ? (
                            <Badge tone={expired ? 'red' : 'amber'}>{toISODateUTC(d.expires_at)}</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-slate-600 max-w-[220px] truncate">
                          {d.notes || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-2 text-right">
                          <Button variant="secondary" className="px-2 py-1 rounded-lg text-xs" onClick={() => openEditDisciplineRow(d)}>
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {!discLic.length && !loading ? (
                    <tr><td colSpan={6} className="p-3 text-center text-slate-500">No discipline licenses.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {/* Instrument */}
          <div className="bg-white border rounded-2xl shadow-sm p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">Instrument licenses</div>
                <div className="text-xs text-slate-500 mt-1">
                  Applies only to GLOBAL templates (<span className="font-mono">organization_id IS NULL</span>).
                </div>
              </div>
              <Badge tone="slate">upsert</Badge>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-6 gap-2">
              <div className="sm:col-span-3">
                <label className="text-[11px] text-slate-600">Global instrument</label>
                <select
                  className="w-full px-3 py-2 rounded-xl border bg-white"
                  value={pickInstrumentId}
                  onChange={(e) => setPickInstrumentId(e.target.value)}
                >
                  <option value="">Select global instrument…</option>
                  {globalList.map((i) => (
                    <option key={i.id} value={i.id}>
                      [{i.form_kind}] {i.instrument_code} v{i.version} — {i.name}{i.license_required ? '' : ' (free)'}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-1">
                <label className="text-[11px] text-slate-600">Active</label>
                <select
                  className="w-full px-3 py-2 rounded-xl border"
                  value={instrActive ? 1 : 0}
                  onChange={(e) => setInstrActive(Number(e.target.value))}
                >
                  <option value={1}>active</option>
                  <option value={0}>inactive</option>
                </select>
              </div>

              <div className="sm:col-span-1">
                <label className="text-[11px] text-slate-600">Starts</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={instrStarts}
                  onChange={(e) => setInstrStarts(e.target.value)}
                />
              </div>

              <div className="sm:col-span-1">
                <label className="text-[11px] text-slate-600">Expires</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={instrExpires}
                  onChange={(e) => setInstrExpires(e.target.value)}
                />
              </div>

              <div className="sm:col-span-6">
                <label className="text-[11px] text-slate-600">Notes</label>
                <input
                  className="w-full px-3 py-2 rounded-xl border"
                  value={instrNotes}
                  onChange={(e) => setInstrNotes(e.target.value)}
                  placeholder="Optional notes…"
                />
              </div>

              <div className="sm:col-span-6 flex justify-end">
                <Button variant="primary" onClick={() => upsertInstrument()} disabled={loading || !pickInstrumentId}>
                  Save instrument license
                </Button>
              </div>
            </div>

            <div className="mt-3 border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-2 text-left">Instrument</th>
                    <th className="p-2 text-left">Active</th>
                    <th className="p-2 text-left">Starts</th>
                    <th className="p-2 text-left">Expires</th>
                    <th className="p-2 text-left">Notes</th>
                    <th className="p-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {instrLic.map((l) => {
                    const expired = isExpiredUtc(l.expires_at);
                    return (
                      <tr key={l.id || `${l.instrument_id}`} className="border-b">
                        <td className="p-2">
                          <div className="font-mono text-[12px] text-slate-700">
                            {l.instrument_code} v{l.version}
                          </div>
                          <div className="text-slate-900 font-medium">{l.name}</div>
                          <div className="text-xs text-slate-500">
                            {l.form_kind} • {l.discipline_code || '—'}
                          </div>
                        </td>
                        <td className="p-2">
                          {l.is_active ? <Badge tone="green">active</Badge> : <Badge tone="red">inactive</Badge>}
                        </td>
                        <td className="p-2">
                          {l.starts_at ? <span className="text-slate-700">{toISODateUTC(l.starts_at)}</span> : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-2">
                          {l.expires_at ? (
                            <Badge tone={expired ? 'red' : 'amber'}>{toISODateUTC(l.expires_at)}</Badge>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="p-2 text-xs text-slate-600 max-w-[220px] truncate">
                          {l.notes || <span className="text-slate-400">—</span>}
                        </td>
                        <td className="p-2 text-right">
                          <Button variant="secondary" className="px-2 py-1 rounded-lg text-xs" onClick={() => openEditInstrumentRow(l)}>
                            Edit
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {!instrLic.length && !loading ? (
                    <tr><td colSpan={6} className="p-3 text-center text-slate-500">No instrument licenses.</td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>

            <div className="mt-3 text-xs text-slate-500">
              Reminder: discipline gating still applies. If the instrument has a <span className="font-mono">discipline_code</span>,
              org also needs an active discipline license for that code.
            </div>
          </div>
        </div>
      )}

      {/* Edit discipline modal */}
      <Modal
        open={editDiscOpen}
        title={editDisc ? `Edit discipline license: ${editDisc.discipline_code}` : 'Edit discipline license'}
        onClose={() => { setEditDiscOpen(false); setEditDisc(null); }}
      >
        {!editDisc ? null : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Discipline code</label>
                <input className="w-full px-3 py-2 rounded-xl border bg-slate-50"
                  value={editDisc.discipline_code}
                  disabled
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">Active</label>
                <select
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editDisc.is_active ? 1 : 0}
                  onChange={(e) => setEditDisc((p) => ({ ...p, is_active: Number(e.target.value) }))}
                >
                  <option value={1}>active</option>
                  <option value={0}>inactive</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Starts</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editDisc.starts_at ? toISODateUTC(editDisc.starts_at) : ''}
                  onChange={(e) => setEditDisc((p) => ({ ...p, starts_at: e.target.value ? `${e.target.value}T00:00:00Z` : null }))}
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">Expires</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editDisc.expires_at ? toISODateUTC(editDisc.expires_at) : ''}
                  onChange={(e) => setEditDisc((p) => ({ ...p, expires_at: e.target.value ? `${e.target.value}T00:00:00Z` : null }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-slate-600">Notes</label>
                <input
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editDisc.notes || ''}
                  onChange={(e) => setEditDisc((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setEditDiscOpen(false); setEditDisc(null); }} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveEditDisc} disabled={loading}>
                {loading ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit instrument modal */}
      <Modal
        open={editInstrOpen}
        title={editInstr ? `Edit instrument license: ${editInstr.instrument_code} v${editInstr.version}` : 'Edit instrument license'}
        onClose={() => { setEditInstrOpen(false); setEditInstr(null); }}
      >
        {!editInstr ? null : (
          <div className="space-y-3">
            <div className="border rounded-xl p-3 bg-slate-50">
              <div className="font-mono text-[12px] text-slate-700">
                {editInstr.instrument_code} v{editInstr.version}
              </div>
              <div className="font-semibold text-slate-900">{editInstr.name}</div>
              <div className="text-xs text-slate-500">
                {editInstr.form_kind} • {editInstr.discipline_code || '—'}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-600">Active</label>
                <select
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editInstr.is_active ? 1 : 0}
                  onChange={(e) => setEditInstr((p) => ({ ...p, is_active: Number(e.target.value) }))}
                >
                  <option value={1}>active</option>
                  <option value={0}>inactive</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-slate-600">Notes</label>
                <input
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editInstr.notes || ''}
                  onChange={(e) => setEditInstr((p) => ({ ...p, notes: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">Starts</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editInstr.starts_at ? toISODateUTC(editInstr.starts_at) : ''}
                  onChange={(e) => setEditInstr((p) => ({ ...p, starts_at: e.target.value ? `${e.target.value}T00:00:00Z` : null }))}
                />
              </div>
              <div>
                <label className="text-xs text-slate-600">Expires</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 rounded-xl border"
                  value={editInstr.expires_at ? toISODateUTC(editInstr.expires_at) : ''}
                  onChange={(e) => setEditInstr((p) => ({ ...p, expires_at: e.target.value ? `${e.target.value}T00:00:00Z` : null }))}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => { setEditInstrOpen(false); setEditInstr(null); }} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={saveEditInstr} disabled={loading}>
                {loading ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}



